import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { IntegratedSolutionsProps } from './IntegratedSolutions.data';
import styles from './IntegratedSolutions.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './IntegratedSolutions.transitions';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import SolutionCard from './solution-card/SolutionCard';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { isTouch } from '../../../hooks/useIsDesktopDevice';

export default function IntegratedSolutions({
  isVisible,
  className,
  blok,
  ref,
}: IntegratedSolutionsProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.integratedSolutions, className, 'max-content-width')}
      {...sbEditable(blok)}
    >
      <div className={styles.header}>
        <Heading
          as="h3"
          size="heading-03"
          disableTriggerInOnScroll={false}
          className={styles.title}
          transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
        >
          {blok.title}
        </Heading>
        <Text
          as="p"
          size="body-standard"
          disableTriggerInOnScroll={false}
          className={classNames(styles.subtitle)}
          transitionInType={isTouch ? 'none' : 'splitLineRotateIn'}
        >
          {blok.subtitle}
        </Text>
      </div>

      <div className={styles.cardsContainer}>
        {blok.cards.map((card) => (
          <SolutionCard blok={card} key={card._uid} className={styles.card} />
        ))}
      </div>
    </div>
  );
}
